import { ReactNode, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthenticatedLayout } from '../components/layout';
import { LoadingScreen } from '../components/ui';
import { useGroups } from '@/contexts/GroupsContext';
import { useCurrentHome } from '@/contexts/HomesContext';
import { PageChromeProvider } from '@/contexts';
import { ProcessingCenterProvider, type ProcessingScope } from '@/components/shared/processing';
import { route } from './paths';
import { AccountSettingsDialogHost } from './AccountSettingsDialogHost';

interface ProtectedRouteProps {
  children?: ReactNode;
  requireHomes?: boolean;
  requireGroups?: boolean;
  bare?: boolean;
}

export function ProtectedRoute({
  children,
  requireHomes = true,
  requireGroups = true,
  bare = false,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const { loading: homesLoading } = useCurrentHome();
  const { loading: groupsLoading } = useGroups();
  const location = useLocation();
  const { homeId, groupId } = useParams<{ homeId?: string; groupId?: string }>();
  const processingScope = useMemo<ProcessingScope | null>(() => {
    if (homeId) return { kind: 'home', id: homeId };
    if (groupId) return { kind: 'group', id: groupId };
    return null;
  }, [groupId, homeId]);

  // Always wait for auth, optionally wait for homes/groups
  if (loading || (requireHomes && homesLoading) || (requireGroups && groupsLoading)) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to={route('signIn')} replace state={{ from: location }} />;
  }

  if (bare) {
    return <>{children ?? <Outlet />}</>;
  }

  return (
    <ProcessingCenterProvider scope={processingScope}>
      <PageChromeProvider>
        <AuthenticatedLayout user={user}>
          {children ?? <Outlet />}
        </AuthenticatedLayout>
        <AccountSettingsDialogHost />
      </PageChromeProvider>
    </ProcessingCenterProvider>
  );
}
